import { Typography } from "@mui/material";
import type {
  GridColDef,
  GridColumnVisibilityModel,
  GridGroupingColDefOverride,
  GridRowGroupingModel,
} from "@mui/x-data-grid-premium";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import { useState } from "react";
import DataGrid from "@entrilia/shared/components/grid/DataGrid";
import {
  DataGridColumnHeadersWithToolbar,
  DataGridHeaderToolbarProvider,
} from "@entrilia/shared/components/grid/DataGridColumnHeadersWithToolbar";
import DataGridGroupHeader from "@entrilia/shared/components/grid/DataGridGroupHeader";
import { getRowSelectionModelFromIds, getSelectedRowIdsFromModel } from "@entrilia/shared/components/grid/gridHelper";
import { useGridGroupsExpanded } from "@entrilia/shared/components/grid/gridHooks";
import useDefinedContext from "@entrilia/shared/hooks/useDefinedContext";
import { convertISODateTime } from "@entrilia/shared/utilities/dateUtils";
import { formatFileSize } from "@entrilia/shared/utilities/fileHelper";
import { getCheckedGridSx } from "../../../../../common/grid/gridStyles";
import FundraisingDocumentActionsCell from "./FundraisingDocumentActionsCell";
import FundraisingDocumentsContext from "./FundraisingDocumentsContext";
import FundraisingDocumentSectionActionsCell from "./FundraisingDocumentSectionActionsCell";
import { AccessOptionsSelect, CategorySelectCell, FileNameCell } from "./fundraisingDocumentsGridProvider";
import FundraisingDocumentsGridToolbar from "./FundraisingDocumentsGridToolbar";
import type { FundraisingDocument } from "./fundraisingDocumentsState";
import { isChangeAccessOptionsAllowed } from "./fundraisingDocumentsState";

const gridRowGroupingModel: GridRowGroupingModel = ["section"];

const gridColumnVisibilityModel: GridColumnVisibilityModel = {
  name: false,
  section: false,
};

const groupingColumnDefinition: GridGroupingColDefOverride<FundraisingDocument> = {
  leafField: "name",
  hideDescendantCount: true,
  renderHeader: (params) => <DataGridGroupHeader params={params} />,
  flex: 2,
};

const columnDefinitions: GridColDef<FundraisingDocument>[] = [
  {
    field: "name",
    headerName: "Name",
    cellClassName: "highlighted-action",
    renderCell: ({ row, rowNode }) => (rowNode.type === "group" ? null : <FileNameCell row={row} />),
  },
  {
    field: "section",
    renderCell: ({ rowNode, colDef }) => {
      if (rowNode.type === "group") {
        return colDef.field === "section" ? null : <Typography variant="subtitle2">{rowNode.groupingKey}</Typography>;
      }

      return null;
    },
  },
  {
    field: "externalCategoryId",
    headerName: "Category",
    flex: 1,
    renderCell: ({ row, rowNode }) => {
      if (rowNode.type === "group") {
        return null;
      }

      return <CategorySelectCell row={row} />;
    },
  },
  {
    field: "accessOptions",
    headerName: "File Access",
    flex: 1,
    minWidth: 160,
    renderCell: ({ row, rowNode }) =>
      rowNode.type === "group" ? null : isChangeAccessOptionsAllowed(row) ? (
        <AccessOptionsSelect row={row} />
      ) : (
        "Download"
      ),
  },
  {
    field: "size",
    headerName: "Size",
    flex: 1,
    minWidth: 160,
    renderCell: ({ row, rowNode }) =>
      rowNode.type === "group" ? null : (
        <Typography color={row.uploadStatus === undefined ? "textPrimary" : "textSecondary"}>
          {formatFileSize(row.size)}
        </Typography>
      ),
  },
  {
    field: "uploadedAt",
    headerName: "Uploaded at",
    flex: 1,
    minWidth: 160,
    renderCell: ({ row, rowNode }) =>
      rowNode.type === "group" || !row.uploadedAt ? null : (
        <Typography color={row.uploadStatus === undefined ? "textPrimary" : "textSecondary"}>
          {convertISODateTime(row.uploadedAt)}
        </Typography>
      ),
  },
  {
    field: "actions",
    headerName: "Actions",
    width: 80,
    align: "right",
    headerAlign: "right",
    cellClassName: "grid-row-actions",
    renderCell: ({ row, rowNode }) =>
      rowNode.type === "group" ? (
        <FundraisingDocumentSectionActionsCell rowNode={rowNode} />
      ) : (
        <FundraisingDocumentActionsCell row={row} />
      ),
  },
];

const getRowId = (row: FundraisingDocument) => row.fileId;

const isRowSelectable = ({ row }: { row: FundraisingDocument }) =>
  !!row.fileId &&
  (row.uploadStatus === undefined || row.uploadStatus === "error" || row.uploadStatus === "ready_for_upload");

const FundraisingDocumentsGrid = () => {
  const apiRef = useGridApiRef();
  const { filteredDocuments, documentsState } = useDefinedContext(FundraisingDocumentsContext);
  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);

  const { isGroupExpanded } = useGridGroupsExpanded(apiRef);

  const showToolbar = selectedRowIds.length > 0;

  return (
    <DataGridHeaderToolbarProvider
      showToolbar={showToolbar}
      selection={[selectedRowIds, setSelectedRowIds]}
      actions={<FundraisingDocumentsGridToolbar selectedIds={selectedRowIds} disabled={documentsState.isSaving} />}
    >
      <DataGrid<FundraisingDocument>
        apiRef={apiRef}
        getRowId={getRowId}
        rows={filteredDocuments}
        columns={columnDefinitions}
        groupingColDef={groupingColumnDefinition}
        rowGroupingModel={gridRowGroupingModel}
        columnVisibilityModel={gridColumnVisibilityModel}
        sx={getCheckedGridSx}
        noRowsText="No files"
        slots={{ columnHeaders: DataGridColumnHeadersWithToolbar }}
        checkboxSelection
        rowSelectionModel={getRowSelectionModelFromIds(selectedRowIds)}
        onRowSelectionModelChange={(selection) => setSelectedRowIds(getSelectedRowIdsFromModel(selection))}
        isGroupExpandedByDefault={isGroupExpanded}
        isRowSelectable={isRowSelectable}
        disableColumnPinning
        disableColumnReorder
        disableColumnSorting
        noScrollableContainer
      />
    </DataGridHeaderToolbarProvider>
  );
};

export default FundraisingDocumentsGrid;
